import { basicSettings } from '../constants';

/**
 * Replaces not allowed characters in string parameter with '.' and shortens the string if it exceeds maxLength
 *
 * @param { String } value - string parameter value
 * @param { Object } parameterInfo - parameter object for given value in basicSettings
 *
 * @return { String } - string parameter value
 */
const cleanupStringParameter = (value, parameterInfo, logger) => {
  // FIXME: replacing unwanted chars with '.' how useful is it / does a parameter need a specific replacement value?
  let result = value.replace(new RegExp(`[^${parameterInfo.allowed}]`, 'g'), '.');
  if (parameterInfo.maxLength && result.length > parameterInfo.maxLength) {
    logger.debug(`Init-parameter ${parameterInfo.name} with length: ${result.length} exceeds maxLength of: ${parameterInfo.maxLength}! String got shortened.`);
    console.warn(`[IAM] ${parameterInfo.name} got shortened`);
    result = `${result.substr(0, parameterInfo.maxLength)}+`;
  }
  return result;
};

/**
 * Replaces not matching enum parameter values with default value
 *
 * @param { String } value - enum parameter value
 * @param { Object } parameterInfo - parameter object for given value in basicSettings
 *
 * @returns { String } - enum parameter value
 */
const cleanupStringEnumParameter = (value, parameterInfo, logger) => {
  let result = value;
  if (new RegExp(`^(${parameterInfo.allowed})$`, 'g').test(result) === false) {
    logger.debug(`Init-parameter ${parameterInfo.name} got wrong input: ${value} allowed: ${parameterInfo.allowed}! Setting to default value: ${parameterInfo.default}`);
    console.warn(`[IAM] ${parameterInfo.name} got invalid input`);
    result = parameterInfo.default;
  }
  return result;
};

/**
 * Create debug and warning output if parameter has invalid type
 *
 * @param {*} value - value given for parameter
 * @param {*} parameterInfo - parameter object for given value in basicSettings
 */
const writeInvalidTypeErrorOutput = (value, parameterInfo, logger) => {
  logger.debug(`Init parameter ${parameterInfo.name} got wrong type of ${typeof value}! Setting to default value: ${parameterInfo.default}`);
  console.warn(`[IAM] ${parameterInfo.name} got invalid input`);
};

/**
 * Checks value type against parameterInfo, set default value if value type does not match or is not clearly convertible
 *
 * @param {*} value - parameter value
 * @param { Object } parameterInfo - parameter object for given value in basicSettings
 *
 * @return {*} - parameter value
 */
const checkSingleParameter = (value, parameterInfo, logger) => {
  let result = value;
  if (parameterInfo.type === 'string') {
    if (typeof result !== 'string' || result === '') {
      writeInvalidTypeErrorOutput(result, parameterInfo, logger);
      result = parameterInfo.default;
    } else {
      result = cleanupStringParameter(result, parameterInfo, logger);
    }
  } else if (parameterInfo.type === 'boolean') {
    if (typeof result !== 'boolean') {
      if (typeof result === 'number' && result >= 0 && result <= 1) {
        result = result !== 0;
      } else {
        writeInvalidTypeErrorOutput(result, parameterInfo, logger);
        result = parameterInfo.default;
      }
    }
  } else if (parameterInfo.type === 'number') {
    if (typeof result !== 'number') {
      writeInvalidTypeErrorOutput(result, parameterInfo, logger);
      result = parameterInfo.default;
    }
  } else if (parameterInfo.type === 'stringEnum') {
    if (typeof result === 'string') {
      result = cleanupStringEnumParameter(result, parameterInfo, logger);
    } else {
      writeInvalidTypeErrorOutput(result, parameterInfo, logger);
      result = parameterInfo.default;
    }
  } else {
    logger.debug(`Init parameter ${parameterInfo.name} has undefined type value of '${parameterInfo.type}'! Setting to default value: ${parameterInfo.default}`);
    result = parameterInfo.default;
  }

  return result;
};

/**
 * Resolves single parameter, takes input from long or short parameter name
 *
 * @param { Object } sensorParameter - all given sensor parameter
 * @param { Object } parameterInfo - parameter object info to check for
 *
 * @returns { value: Object, stopSensorInit: Boolean } - parameter value, if sensor should stop init
 */
const resolveSingleParameter = (sensorParameter, parameterInfo, logger) => {
  let value;
  if (sensorParameter[parameterInfo.name] === undefined) {
    if (sensorParameter[parameterInfo.short] === undefined) {
      logger.debug(`Setting ${parameterInfo.name} to default: ${parameterInfo.default}`);
      return { value: parameterInfo.default, stopSensorInit: parameterInfo.strictRequired };
    }
    value = sensorParameter[parameterInfo.short];
  } else {
    value = sensorParameter[parameterInfo.name];
    if (sensorParameter[parameterInfo.short] !== undefined) {
      logger.debug(`Init parameter ${parameterInfo.name} was set twice. ${parameterInfo.name}: ${sensorParameter[parameterInfo.name]};
        ${parameterInfo.short}: ${sensorParameter[parameterInfo.short]}. Using ${sensorParameter[parameterInfo.name]}`);
      console.warn(`Init parameter ${parameterInfo.name} is set twice`);
    }
  }
  const checkedValue = checkSingleParameter(value, parameterInfo, logger);
  let valueCheckStopsSensor = false;
  if (
    parameterInfo.strictRequired === true
    && (checkedValue !== value || value === '')
  ) {
    valueCheckStopsSensor = true;
  }
  return { value: checkedValue, stopSensorInit: valueCheckStopsSensor };
};

/**
 * Public function to resolve a single parameter in sensor parameter
 *
 * @param { Object } sensorParameter - all given sensor parameter
 * @param { String } parameterLongName - long name of parameter to check for
 *
 * @returns { * | undefined} - resolved parameter value | 'undefined' if parameterLongName has no entry in basicSettings
 */
const resolveParameter = (sensorParameter, parameterLongName, logger) => {
  const parameterInfo = basicSettings.initParameter[parameterLongName];
  if (parameterInfo !== undefined) {
    const { value } = resolveSingleParameter(sensorParameter, basicSettings.initParameter[parameterLongName], logger);
    return value;
  }
  logger.debug(`Init parameter name ${parameterLongName} is unknown. Returning undefined!`);
  return undefined;
};


/**
 * Public function to resolve all sensor parameter
 *
 * @param { Object } sensorParameter - all given sensor parameter
 *
 * @returns  { initParameter: Object, stopSensorInit: Boolean } - all initParameter / if sensor should stop init
 */
const resolveAllParameter = (sensorParameter, logger) => {
  const resolvedResults = {};
  const initParameterKeys = Object.keys(basicSettings.initParameter);
  for (let i = 0, iLen = initParameterKeys.length; i < iLen; i += 1) {
    const parameterInfo = initParameterKeys[i];
    const { value, stopSensorInit } = resolveSingleParameter(sensorParameter, basicSettings.initParameter[parameterInfo], logger);
    if (stopSensorInit === true) {
      logger.debug(`Sending stop senor event from init parameter resolver, cause by key: ${basicSettings.initParameter[parameterInfo].name}, got value: ${value}`);
      return { initParameter: {}, stopSensorInit };
    }
    resolvedResults[parameterInfo] = value;
  }
  return { initParameter: resolvedResults, stopSensorInit: false };
};

/**
 * Resolves all special parameters
 *
 * @param {Object} sensorParameter
 * @param {Object} systemConfig
 * @param {Object} logger
 * @return {Object} Resolved special parameters
 */
const resolveSpecialParameter = (sensorParameter, systemConfig, logger) => {
  const resolvedResults = {};
  const specialParameterKeys = Object.keys(basicSettings.specialParameter);
  for (let i = 0, iLen = specialParameterKeys.length; i < iLen; i += 1) {
    const parameterInfo = basicSettings.specialParameter[specialParameterKeys[i]];
    if (systemConfig.specialParameters && systemConfig.specialParameters[parameterInfo.condition.key] === parameterInfo.condition.value) {
      const { value } = resolveSingleParameter(sensorParameter, parameterInfo, logger);
      resolvedResults[parameterInfo.name] = value;
    }
  }
  return resolvedResults;
};

export { resolveAllParameter, resolveParameter, resolveSpecialParameter };
